import { useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { openCalendlyPopup } from "@/lib/calendly";
import { useAuth } from "@/context/AuthContext";
import { useT } from "@/lib/i18n";
import { brainTypeReport } from "@/lib/brainTypeReports";
import { Button } from "@/components/ui/button";
import { ArrowLeft, ArrowRight, Brain, Sparkles, ShieldQuestion, Check } from "lucide-react";
import { toast } from "sonner";

/**
 * Quick (Dutch) Brain Type self-test offered to prospects during onboarding.
 *
 * Reliability: ~66% (2 in 3). A coach double-check is always recommended.
 *
 * Decision tree (Q1=oplosser/maker → Q2=A/B/C → optional Q3):
 *   oplosser + A → 9
 *   oplosser + B → Q3 fun/risico → 7 / 6
 *   oplosser + C → 3
 *   maker + A    → Q3 mijzelf/anderen → 1 / 8
 *   maker + B    → 5
 *   maker + C    → Q3 plezier/melancholisch → 2 / 4
 */

const Q1 = {
  text:
    "Ben je een probleemoplosser of een probleemmaker? Let wel, probleemoplossers maken ook problemen, maar zijn meer bereid om een compromis te maken omwille van de lieve vrede. Probleemmakers lossen ook problemen op, maar zijn minder bereid tot een compromis. Een compromis mag niet ten koste gaan van bijvoorbeeld fouten, onrecht of de underdog.",
  options: [
    { value: "oplosser", label: "Probleemoplosser" },
    { value: "maker", label: "Probleemmaker" },
  ],
};

const Q2 = {
  text:
    "Wat past het beste bij jou? A) Je bent een doener en een woede type. Je gaat ervan uit dat macht gewoon macht is. Of je past macht direct toe als controlfreak of dominatie of indirect door middel van het masseren van mensen. B) Je bent een denker en een angst type. Je gaat ervan uit dat kennis = macht. C) Je wil graag dat mensen van je houden en je bent een verdriet type. Je gaat ervan uit dat liefde = macht.",
  options: [
    { value: "A", label: "A · Doener / Woede" },
    { value: "B", label: "B · Denker / Angst" },
    { value: "C", label: "C · Geliefd / Verdriet" },
  ],
};

function getQ3(a1, a2) {
  if (a1 === "oplosser" && a2 === "B") {
    return {
      text: "Ben je vooral gericht op fun of juist op risico en/of gevaar?",
      options: [
        { value: "fun", label: "Fun" },
        { value: "risico", label: "Risico / Gevaar" },
      ],
    };
  }
  if (a1 === "maker" && a2 === "A") {
    return {
      text:
        "Ben je iemand die eerst boos op zichzelf wordt en daarna pas op anderen? Of vind je jezelf okay en ben je vooral boos op anderen?",
      options: [
        { value: "mijzelf", label: "Vooral boos op mijzelf" },
        { value: "anderen", label: "Vooral boos op anderen" },
      ],
    };
  }
  if (a1 === "maker" && a2 === "C") {
    return {
      text: "Ben je vooral gericht op plezier maken of ben je meer melancholisch?",
      options: [
        { value: "plezier", label: "Plezier" },
        { value: "melancholisch", label: "Melancholisch" },
      ],
    };
  }
  return null;
}

function decide(a1, a2, a3) {
  if (a1 === "oplosser") {
    if (a2 === "A") return 9;
    if (a2 === "B") return a3 === "fun" ? 7 : 6;
    if (a2 === "C") return 3;
  }
  if (a1 === "maker") {
    if (a2 === "A") return a3 === "mijzelf" ? 1 : 8;
    if (a2 === "B") return 5;
    if (a2 === "C") return a3 === "plezier" ? 2 : 4;
  }
  return null;
}

function QuestionCard({ stepLabel, text, options, value, onSelect, onBack, canBack, testidPrefix }) {
  const { t } = useT();
  return (
    <div data-testid={`${testidPrefix}-question`}>
      <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#8A9188] mb-4">
        {stepLabel}
      </p>
      <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl tracking-tight text-[#1A1D1A] font-light leading-[1.1]">
        {t("bt_test.title")}
      </h1>
      <p className="mt-6 text-[#5C635A] leading-relaxed max-w-2xl text-base">{text}</p>

      <div className="mt-10 space-y-3 max-w-2xl" data-testid={`${testidPrefix}-options`}>
        {options.map((o) => {
          const active = value === o.value;
          return (
            <button
              key={o.value}
              type="button"
              onClick={() => onSelect(o.value)}
              data-testid={`${testidPrefix}-option-${o.value}`}
              className={`w-full text-left h-auto py-5 px-6 rounded-xl border transition-colors flex items-center gap-4 ${
                active
                  ? "bg-[#4A5D4E] border-[#4A5D4E] text-white"
                  : "bg-white border-[#E5E2DC] text-[#1A1D1A] hover:bg-[#F2F0EA]"
              }`}
            >
              <span
                className={`h-6 w-6 rounded-full border flex items-center justify-center shrink-0 ${
                  active ? "border-white" : "border-[#B37A5F]"
                }`}
              >
                {active && <Check strokeWidth={2} size={14} />}
              </span>
              <span className="font-medium">{o.label}</span>
            </button>
          );
        })}
      </div>

      {canBack && (
        <div className="mt-10">
          <Button
            type="button"
            variant="outline"
            onClick={onBack}
            data-testid={`${testidPrefix}-back-button`}
            className="h-11 rounded-lg border-[#E5E2DC] bg-transparent text-[#1A1D1A] hover:bg-[#F2F0EA]"
          >
            <ArrowLeft strokeWidth={1.5} size={18} className="mr-2" />
            {t("bt_test.previous")}
          </Button>
        </div>
      )}
    </div>
  );
}

export default function BrainTypeQuickTest({ onComplete }) {
  const { user } = useAuth();
  const { t } = useT();
  const [step, setStep] = useState(0); // 0=Q1, 1=Q2, 2=Q3 (if needed), 3=result
  const [a1, setA1] = useState(null);
  const [a2, setA2] = useState(null);
  const [a3, setA3] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [requesting, setRequesting] = useState(false);
  const [requested, setRequested] = useState(false);

  const q3 = a1 && a2 ? getQ3(a1, a2) : null;
  const needsQ3 = Boolean(q3);
  const preliminary = !needsQ3 && a1 && a2 ? decide(a1, a2) : a3 ? decide(a1, a2, a3) : null;

  const pickA1 = (v) => {
    setA1(v);
    setA2(null);
    setA3(null);
    setStep(1);
  };
  const pickA2 = (v) => {
    setA2(v);
    setA3(null);
    // If needs Q3, advance to Q3 step (2). Otherwise go straight to result (step 3).
    const needs = Boolean(getQ3(a1, v));
    setStep(needs ? 2 : 3);
  };
  const pickA3 = (v) => {
    setA3(v);
    setStep(3);
  };

  const saveResult = async () => {
    if (!preliminary) return null;
    setSubmitting(true);
    try {
      await api.post("/onboarding/me/preliminary-brain-type", {
        preliminary_brain_type: preliminary,
      });
      return true;
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not save brain type");
      return false;
    } finally {
      setSubmitting(false);
    }
  };

  const requestDoubleCheck = async () => {
    setRequesting(true);
    try {
      await saveResult();
      await api.post("/qol/me/request-coaching", {
        reason: "brain_type_double_check",
        note: `User completed the quick brain type test. Preliminary result: type ${preliminary}.`,
      });
      setRequested(true);
      toast.success(t("bt_test.toast.requested"));
      // Open Calendly so the client can book the double-check meeting directly.
      openCalendlyPopup(user?.coach_calendly_embed_code || user?.coach_calendly_url);
      // Finish onboarding flow after a short pause so user reads the confirmation
      setTimeout(() => onComplete?.(), 800);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || t("bt_test.toast.could_not_send"));
    } finally {
      setRequesting(false);
    }
  };

  const continueWithout = async () => {
    const ok = await saveResult();
    if (ok) onComplete?.();
  };

  if (step === 0) {
    return (
      <QuestionCard
        stepLabel={t("bt_test.step_label_q1")}
        text={Q1.text}
        options={Q1.options}
        value={a1}
        onSelect={pickA1}
        canBack={false}
        testidPrefix="bt-test-q1"
      />
    );
  }

  if (step === 1) {
    return (
      <QuestionCard
        stepLabel={t("bt_test.step_label_q2")}
        text={Q2.text}
        options={Q2.options}
        value={a2}
        onSelect={pickA2}
        onBack={() => {
          setStep(0);
          setA2(null);
        }}
        canBack
        testidPrefix="bt-test-q2"
      />
    );
  }

  if (step === 2 && q3) {
    return (
      <QuestionCard
        stepLabel={t("bt_test.step_label_q3")}
        text={q3.text}
        options={q3.options}
        value={a3}
        onSelect={pickA3}
        onBack={() => {
          setStep(1);
          setA3(null);
        }}
        canBack
        testidPrefix="bt-test-q3"
      />
    );
  }

  // Result screen
  // Preview only the first two paragraphs of the first section of the
  // narrative report — keeps the unlock value of the double-check intact
  // while still rewarding the user with a glimpse of their type.
  const previewParagraphs = (() => {
    const report = brainTypeReport(preliminary);
    if (!report || !report.length) return [];
    const firstSection = report[0];
    if (!firstSection || !firstSection.body) return [];
    return firstSection.body.split(/\n\s*\n/).slice(0, 2);
  })();

  return (
    <div data-testid="bt-test-result">
      <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#8A9188] mb-4">
        {t("bt_test.result_overline")}
      </p>
      <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl tracking-tight text-[#1A1D1A] font-light leading-[1.1]">
        {t("bt_test.result_title_a")}{" "}
        <span className="font-semibold">#{preliminary}</span>
      </h1>

      {/* Preview — first 2 paragraphs of the first report section. No graphic. */}
      {previewParagraphs.length > 0 && (
        <div
          className="mt-8 bg-white border border-[#E5E2DC] rounded-2xl p-6 sm:p-8 max-w-3xl"
          data-testid="bt-test-preview"
        >
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#B37A5F] mb-4">
            {t("bt_test.preview.overline")}
          </p>
          <div className="space-y-4 text-[#2A2D2A] leading-relaxed">
            {previewParagraphs.map((p, i) => (
              <p key={i} data-testid={`bt-test-preview-paragraph-${i}`}>
                {p}
              </p>
            ))}
          </div>
          <p
            className="mt-6 pt-5 border-t border-[#E5E2DC] text-sm text-[#5C635A] leading-relaxed italic"
            data-testid="bt-test-preview-disclaimer"
          >
            {t("bt_test.preview.disclaimer")}
          </p>
        </div>
      )}

      <div
        className="mt-8 bg-[#F2F0EA] border border-[#E5E2DC] rounded-2xl p-6 sm:p-8 flex items-start gap-4 max-w-3xl"
        data-testid="bt-test-disclaimer"
      >
        <div className="h-11 w-11 rounded-xl bg-white flex items-center justify-center shrink-0 border border-[#E5E2DC]">
          <ShieldQuestion strokeWidth={1.5} className="text-[#B37A5F]" size={20} />
        </div>
        <div>
          <h3 className="font-heading text-xl text-[#1A1D1A] tracking-tight font-semibold mb-2">
            {t("bt_test.disclaimer_title")}
          </h3>
          <p className="text-sm text-[#5C635A] leading-relaxed">
            {t("bt_test.disclaimer_body")}
          </p>
        </div>
      </div>

      <div className="mt-10 max-w-3xl space-y-3">
        <Button
          type="button"
          onClick={requestDoubleCheck}
          disabled={requesting || submitting || requested}
          data-testid="bt-test-request-double-check-button"
          className="w-full sm:w-auto h-12 rounded-lg bg-[#4A5D4E] hover:bg-[#3D4D41] text-white px-6"
        >
          <Sparkles strokeWidth={1.5} size={18} className="mr-2" />
          {requested
            ? t("bt_test.requested")
            : requesting
            ? t("bt_test.requesting")
            : t("bt_test.request_double_check")}
        </Button>
        <Button
          type="button"
          variant="outline"
          onClick={continueWithout}
          disabled={requesting || submitting || requested}
          data-testid="bt-test-continue-button"
          className="w-full sm:w-auto h-12 rounded-lg border-[#E5E2DC] bg-transparent text-[#1A1D1A] hover:bg-[#F2F0EA] px-6 sm:ml-3"
        >
          {submitting ? t("bt_test.saving") : t("bt_test.continue_without")}
          <ArrowRight strokeWidth={1.5} size={18} className="ml-2" />
        </Button>
      </div>

      <div className="mt-10">
        <Button
          type="button"
          variant="outline"
          onClick={() => {
            // Step back to last answered question
            setStep(needsQ3 ? 2 : 1);
          }}
          data-testid="bt-test-result-back-button"
          className="h-11 rounded-lg border-[#E5E2DC] bg-transparent text-[#5C635A] hover:bg-[#F2F0EA]"
        >
          <ArrowLeft strokeWidth={1.5} size={18} className="mr-2" />
          {t("bt_test.retake_last")}
        </Button>
      </div>
    </div>
  );
}
